import { FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { ErrorCodeManagementInjection } from "features/errorCodeManagement/di/ErrorCodeManagementInjection";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { ErrorCodeActionRequestEntity } from "../entities/ErrorCodeActionRequestEntity";
import { ErrorCodeRequestEntity } from "../entities/ErrorCodeRequestEntity";
import { ErrorCodeResponseEntity } from "../entities/ErrorCodeResponseEntity";
import { IErrorCodeManagementRepository } from "../repositories/IErrorCodeManagementRepository";

@injectable()
export class ErrorCodeManagementUseCase {
  private errorCodeRepo: IErrorCodeManagementRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.errorCodeRepo = ErrorCodeManagementInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  private checkParams(value: ErrorCodeRequestEntity) {
    let params = {
      ...value,
      code: paramsChecking(value?.code, "input"),
      status: paramsChecking(value?.status, "select"),
      order: value?.order || "createdAt DESC",
      type: paramsChecking(value?.type, "select"),
      message: paramsChecking(value?.message, "input"),
      source: paramsChecking(value?.source, "select"),
      service: paramsChecking(value?.service, "select"),
      description: paramsChecking(value?.description, "input"),
    };
    return params;
  }

  async getList(
    params: ErrorCodeRequestEntity,
  ): Promise<ListBaseApiResponseModel<ErrorCodeResponseEntity>> {
    const updatedParams = this.checkParams(params);
    return await this.errorCodeRepo.getList(updatedParams);
  }

  async getDetail(
    params: ErrorCodeRequestEntity,
  ): Promise<BaseApiResponseModel<ErrorCodeResponseEntity>> {
    return await this.errorCodeRepo.getDetail(params);
  }

  async createErrorCode(
    body: ErrorCodeActionRequestEntity,
  ): Promise<BaseApiResponseModel<ErrorCodeResponseEntity>> {
    return await this.errorCodeRepo.createErrorCode(body);
  }

  async updateErrorCode(
    body: ErrorCodeActionRequestEntity,
  ): Promise<BaseApiResponseModel<ErrorCodeResponseEntity>> {
    return await this.errorCodeRepo.updateErrorCode(body);
  }

  async deleteErrorCode(params: {
    code: string;
  }): Promise<BaseApiResponseModel<ErrorCodeResponseEntity>> {
    return await this.errorCodeRepo.deleteErrorCode(params);
  }

  async getServiceList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.SERVICE_LIST,
      params,
      isNeedFeature: true,
      savedKey: CacheKeyEnum.serviceList,
    });
  }
}
